'use client';
import { useState } from 'react';

import type { EventMarket, EventResponse, Market, Outcome } from '@/types';

const getDefaultOutcomeId = (
  sortedMarkets: EventMarket[],
  rawMarkets: EventMarket[] | undefined
): string | null => {
  if (!rawMarkets || rawMarkets.length === 0) return null;

  if (sortedMarkets.length > 0) {
    const topMarket = sortedMarkets[0];
    if (Array.isArray(topMarket.outcomes) && topMarket.outcomes.length > 0) {
      const yesOutcome = topMarket.outcomes.find((o) => o?.name === 'Yes');
      return (yesOutcome || topMarket.outcomes[0]).id;
    }
    return topMarket.id;
  }

  const firstResolved = rawMarkets.find((m) => m.umaResolutionStatus === 'resolved');
  const marketToUse = firstResolved || rawMarkets[0];

  if (
    marketToUse?.outcomes &&
    Array.isArray(marketToUse.outcomes) &&
    marketToUse.outcomes.length > 0
  ) {
    return marketToUse.outcomes[0].id;
  }

  return marketToUse?.id || null;
};

export const useMarketSelection = (
  event: EventResponse | undefined,
  sortedMarkets: EventMarket[]
) => {
  const [selectedOutcomeId, setSelectedOutcomeId] = useState<string | null>(null);

  const markets = event?.markets;

  const defaultSelectedOutcomeId = getDefaultOutcomeId(sortedMarkets, markets);
  const effectiveSelectedOutcomeId = selectedOutcomeId ?? defaultSelectedOutcomeId;

  const matchingOutcome = markets
    ?.flatMap((market) => (Array.isArray(market.outcomes) ? market.outcomes : []))
    .find((outcome) => outcome?.id === effectiveSelectedOutcomeId);

  const selectedOutcome = matchingOutcome
    ? ({
        ...matchingOutcome,
        price:
          typeof matchingOutcome.price === 'string'
            ? Number(matchingOutcome.price)
            : matchingOutcome.price,
      } as Outcome)
    : null;

  const matchingMarket = markets?.find((m) =>
    Array.isArray(m.outcomes)
      ? m.outcomes.some((o) => o?.id === effectiveSelectedOutcomeId)
      : m.id === effectiveSelectedOutcomeId
  );

  const selectedMarket = (matchingMarket as unknown as Market) || null;

  return {
    selectedOutcomeId,
    setSelectedOutcomeId,
    effectiveSelectedOutcomeId,
    selectedOutcome,
    selectedMarket,
  };
};
